import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Loader2, Search, Mail, Copy, Check } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GAMES = [
  { k: "wager", label: "Draft H2H", color: "#10B981" },
  { k: "shootout", label: "Penalties", color: "#38BDF8" },
  { k: "pep", label: "Pep", color: "#6366F1" },
  { k: "daily", label: "Daily", color: "#F472B6" },
];

const ago = (iso) => {
  if (!iso) return "never";
  const s = (Date.now() - new Date(iso.endsWith("Z") || iso.includes("+") ? iso : `${iso}Z`).getTime()) / 1000;
  if (s < 3600) return `${Math.max(1, Math.round(s / 60))}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const Played = ({ u }) => {
  const chips = GAMES.filter((g) => u.games[g.k]);
  return (
    <div className="flex flex-wrap gap-1.5">
      {chips.map((g) => (
        <span key={g.k} data-testid={`played-${g.k}`} className="rounded-full px-2 py-0.5 text-[11px] font-semibold border" style={{ color: g.color, borderColor: `${g.color}55`, background: `${g.color}12` }}>
          {g.label} {u.games[g.k].played} · {u.games[g.k].won}W
        </span>
      ))}
      {u.spins > 0 && <span className="rounded-full px-2 py-0.5 text-[11px] font-semibold border border-[#F59E0B]/40 text-[#F59E0B] bg-[#F59E0B]/10">Wheel {u.spins}</span>}
      <span data-testid="played-progress" className={`rounded-full px-2 py-0.5 text-[11px] font-semibold border ${u.started ? "border-white/15 text-slate-200" : "border-white/10 text-slate-500"}`}>
        {u.started ? `Started ${u.started} · Finished ${u.finished}` : "Visited only"}
      </span>
    </div>
  );
};

// Admin: newest accounts with emails and what each has played.
export const AdminUsers = ({ adminKey }) => {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);
  const [guests, setGuests] = useState(false);
  const [q, setQ] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setRows(null);
    axios
      .get(`${API}/admin/users`, { params: { include_guests: guests }, headers: { "X-Admin-Key": adminKey } })
      .then((r) => { setRows(r.data.users); setSummary(r.data.summary); })
      .catch(() => setRows([]));
  }, [adminKey, guests]);

  const shown = useMemo(() => {
    const t = q.trim().toLowerCase();
    return (rows || []).filter((u) => !t || `${u.username} ${u.email || ""}`.toLowerCase().includes(t));
  }, [rows, q]);

  const copyEmails = async () => {
    const list = shown.map((u) => u.email).filter(Boolean).join(", ");
    try { await navigator.clipboard.writeText(list); } catch (e) { /* blocked */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <section data-testid="admin-users">
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <h2 className="text-muted-38 text-sm font-semibold mr-auto">Players {rows && <span className="text-slate-500">({shown.length})</span>}</h2>
        <label className="flex items-center gap-1.5 text-xs text-slate-300 cursor-pointer">
          <input data-testid="admin-show-guests" type="checkbox" checked={guests} onChange={(e) => setGuests(e.target.checked)} className="accent-[#10B981]" />
          Show guests
        </label>
        <button data-testid="admin-copy-emails" onClick={copyEmails} className="btn-ghost rounded-lg px-3 py-1.5 text-xs font-semibold text-white flex items-center gap-1.5">
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />} {copied ? "Copied" : "Copy emails"}
        </button>
      </div>
      {summary && (
        <div data-testid="admin-play-summary" className="grid grid-cols-3 gap-2 mb-3 text-center">
          {[["Visited only", summary.visited_only, "text-slate-400"], ["Started, didn't finish", summary.started_not_finished, "text-[#F59E0B]"], ["Finished a game", summary.finished, "text-[#10B981]"]].map(([l, v, c]) => (
            <div key={l} className="card-38 p-2.5 hover:translate-y-0">
              <div className={`font-display font-bold text-xl ${c}`}>{v}</div>
              <div className="text-[10px] text-slate-400 leading-tight">{l}</div>
            </div>
          ))}
        </div>
      )}
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
        <input data-testid="admin-user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search username or email" className="w-full rounded-xl bg-white/5 border border-white/10 pl-9 pr-3 py-2.5 text-sm text-white placeholder-gray-500 outline-none focus:border-[#10B981]/60" />
      </div>
      {!rows ? (
        <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-[#10B981]" /></div>
      ) : !shown.length ? (
        <div className="text-center text-slate-500 text-sm py-6">No players yet.</div>
      ) : (
        <div className="space-y-2">
          {shown.map((u, i) => (
            <div key={`${u.username}-${i}`} data-testid={`admin-user-row-${i}`} className="card-38 p-3 hover:translate-y-0">
              <div className="flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-white truncate" data-testid={`admin-user-name-${i}`}>
                    {u.username} {u.guest && <span className="text-[10px] text-slate-500 font-normal">guest</span>}
                  </div>
                  <div className="text-xs truncate flex items-center gap-1" data-testid={`admin-user-email-${i}`}>
                    <Mail className="w-3 h-3 text-slate-500 shrink-0" />
                    {u.email ? <a href={`mailto:${u.email}`} className="text-[#38BDF8] hover:underline truncate">{u.email}</a> : <span className="text-slate-500">no email</span>}
                  </div>
                </div>
                <div className="text-right shrink-0 text-[11px] text-slate-400 leading-tight">
                  <div>Joined {ago(u.joined)}</div>
                  <div>Played {ago(u.last_played)}</div>
                  <div className="text-[#F59E0B] font-semibold">£{Number(u.coins).toLocaleString()}</div>
                </div>
              </div>
              <div className="mt-2"><Played u={u} /></div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default AdminUsers;
